/**
 * B91 GAP-6 — Stripe Connect custody doors (RULED 2026-09-21, Pathik:
 * "platform account + per-tenant connected accounts, webhooks registered
 * centrally; the platform never holds raw tenant keys").
 *
 * Three ADMIN-only doors, all speaking the platform's Connect port — no
 * request field anywhere carries key material, and no response ever does:
 * - stripe.connect.create — mint (or answer) the tenant's ONE connected
 *   account; idempotent by construction.
 * - stripe.connect.link — a provider-hosted onboarding link; the tenant
 *   finishes setup on the provider's own pages.
 * - stripe.connect.status — where custody stands (absent = honestly none).
 */
import { z } from "zod";
import { readOp, writeOp } from "./common.js";

const zConnectAccount = z
  .object({
    accountId: z.string().min(1),
    chargesEnabled: z.boolean(),
    detailsSubmitted: z.boolean(),
  })
  .strict();

export const stripeConnectCreate = writeOp({
  name: "stripe.connect.create",
  permission: "payment:write",
  principals: ["STAFF:ADMIN"],
  fields: {},
  response: z.object({ account: zConnectAccount }).strict(),
  exampleRequest: {},
  exampleResponse: { account: { accountId: "acct_1Example", chargesEnabled: false, detailsSubmitted: false } },
});

export const stripeConnectLink = writeOp({
  name: "stripe.connect.link",
  permission: "payment:write",
  principals: ["STAFF:ADMIN"],
  fields: {
    /** Where the provider sends the operator when onboarding completes. */
    returnUrl: z.string().min(1).max(2048),
    /** Where the provider sends them when the link expires mid-flow. */
    refreshUrl: z.string().min(1).max(2048),
  },
  response: z.object({ url: z.string().min(1), expiresAt: z.number().int().optional() }).strict(),
  exampleRequest: { returnUrl: "https://admin.example.com/settings/payments?connect=done", refreshUrl: "https://admin.example.com/settings/payments" },
  exampleResponse: { url: "https://connect.stripe.com/setup/s/example", expiresAt: 1_790_000_000_000 },
});

export const stripeConnectStatus = readOp({
  name: "stripe.connect.status",
  permission: "payment:write",
  principals: ["STAFF:ADMIN"],
  fields: {},
  response: z.object({ account: zConnectAccount.optional() }).strict(),
  exampleRequest: {},
  exampleResponse: { account: { accountId: "acct_1Example", chargesEnabled: true, detailsSubmitted: true } },
});
